/**
 * App global ErrorBoundary (Solid `<ErrorBoundary>`).
 *
 * Unlike router's `errorComponent` (which only catches route
 * loading/beforeLoad errors), this catches RENDER errors thrown in
 * any part of the component tree (incl. local queries that
 * fail, transforms, charts, etc.) and NEVER leaves a blank screen.
 *
 * Mounted at two levels:
 *  1. Global, wrapping root `<Outlet />` (final firewall).
 *  2. Per data region, via `<DataErrorBoundary>`, so a single section
 *     failure doesn't crash the whole screen and can be retried in-place.
 *
 * PREMIUM Fallback: theme-aware SVG illustration (currentColor + tokens),
 * title, description, "Retry" (resets boundary) and "Reload". Error
 * is always logged to console.
 */

import {
  type Component,
  createEffect,
  type JSX,
  ErrorBoundary as SolidErrorBoundary,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import { RefreshCw, RotateCw } from "@fleet/icons";
import { cn } from "@cashsync/shared";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

function errorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === "string" && error.length > 0) {
    return error;
  }
  return null;
}

/** Decorative SVG illustration, theme-aware (inherits container color). */
function BrokenChartArt() {
  return (
    <svg class="h-28 w-28 text-primary/70" viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Concentric halo */}
      <circle
        cx="60"
        cy="60"
        r="56"
        stroke="currentColor"
        stroke-width="1"
        class="text-primary/15"
      />
      <circle
        cx="60"
        cy="60"
        r="42"
        stroke="currentColor"
        stroke-width="1"
        class="text-primary/10"
      />
      {/* "Broken" chart bars */}
      <rect
        x="38"
        y="62"
        width="10"
        height="24"
        rx="2"
        fill="currentColor"
        class="text-primary/30"
      />
      <rect
        x="54"
        y="50"
        width="10"
        height="36"
        rx="2"
        fill="currentColor"
        class="text-primary/50"
      />
      <rect
        x="70"
        y="70"
        width="10"
        height="16"
        rx="2"
        fill="currentColor"
        class="text-primary/30"
      />
      {/* Broken line (the "crack") */}
      <path
        d="M34 46 L48 54 L58 40 L70 52"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-destructive"
      />
      <circle cx="58" cy="40" r="3" fill="currentColor" class="text-destructive" />
    </svg>
  );
}

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** `region` shows compact variant (card), not full screen. */
  region?: boolean;
  title?: string;
  description?: string;
  /** Identifier for log (which region failed). */
  label?: string;
}

/** Reusable premium fallback. Always logs error when displayed. */
const ErrorFallback: Component<ErrorFallbackProps> = (props) => {
  const { t } = useI18n();
  const detail = () => errorMessage(props.error);

  createEffect(() => {
    console.error(`[ErrorBoundary${props.label ? `:${props.label}` : ""}]`, props.error);
  });

  return (
    <div
      role="alert"
      class={cn(
        "flex w-full flex-col items-center justify-center gap-5 p-6 text-center",
        props.region
          ? "min-h-[16rem] rounded-xl border border-border/50 bg-card/60"
          : "min-h-[60dvh]",
      )}
    >
      <BrokenChartArt />
      <div class="space-y-2">
        <h2 class={cn("font-bold tracking-tight", props.region ? "text-lg" : "text-2xl")}>
          {props.title ?? t(msg`Something went wrong`)}
        </h2>
        <p class="mx-auto max-w-sm text-sm text-muted-foreground">
          {props.description ?? t(msg`This section couldn't load. Your data is safe — try again.`)}
        </p>
        {detail() ? (
          <p class="mx-auto max-w-sm truncate text-xs text-muted-foreground/70 font-mono">
            {detail()}
          </p>
        ) : null}
      </div>
      <div class="flex flex-wrap items-center justify-center gap-3">
        <Button
          class="gap-2"
          onClick={() => {
            props.reset();
          }}
        >
          <RefreshCw class="h-4 w-4" />
          {t(msg`Retry`)}
        </Button>
        <Button
          variant="outline"
          class="gap-2"
          onClick={() => {
            globalThis.location.reload();
          }}
        >
          <RotateCw class="h-4 w-4" />
          {t(msg`Reload`)}
        </Button>
      </div>
    </div>
  );
};

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Compact variant for data regions. */
  region?: boolean;
  title?: string;
  description?: string;
  /** Identifier for log (which region failed). */
  label?: string;
}

/**
 * Full-screen global boundary. Use around root `<Outlet />`.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  // `fallback` is a Solid render-prop (receives error + reset), not the
  // definition of a nested component; we return the module-level <ErrorFallback>
  // component already defined.
  const renderFallback = (error: unknown, reset: () => void): JSX.Element => (
    <ErrorFallback
      error={error}
      reset={reset}
      region={props.region}
      title={props.title}
      description={props.description}
      label={props.label}
    />
  );
  return <SolidErrorBoundary fallback={renderFallback}>{props.children}</SolidErrorBoundary>;
}

/**
 * REGION boundary: compact card fallback to isolate section
 * failure without crashing entire screen.
 */
export function DataErrorBoundary(props: AppErrorBoundaryProps) {
  return <AppErrorBoundary {...props} region />;
}
